import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { OCF_DEFAULT_CORRECTION_REASON } from "features/cdm/constants/correctionReasonOptions";
import { isCorrectionEligibilityConflict } from "features/cdm/utils/correctionValidation";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useValidateOCF = ({ patientId }) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError("Error while validating PP");
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/cdm/patients/${patientId}/ocf/validate`,
        {
          originalReimbursementMessageControlId:
            data?.reimbursementMessageControlId || null,
          correctionReason: data?.reimbursementMessageControlId
            ? OCF_DEFAULT_CORRECTION_REASON
            : null,
          patientDemographics: {
            ethnicityCode: data.ethnicityCode,
          },
          indications: {
            currentSmokingStatus: data.currentSmokingStatus,
            bmiGreaterThan30: data.bmiGreaterThan30,
            previousHighBnp: data.previousHighBnp,
            highRiskEthnicity: data.highRiskEthnicity,
            historyOfGestationalDiabetes: data.historyOfGestationalDiabetes,
            dyslipidaemia: data.dyslipidaemia,
            chronicKidneyDisease: data.chronicKidneyDisease,
            historyOfSevereMentalIllness: data.historyOfSevereMentalIllness,
            otherText: data.otherText,
          },
          // OCF dataset V1.9 Risk Factors = smoking status, vaping, weight,
          // height, BMI, waist, QRisk3 only. Alcohol, physical activity and all
          // MECC interventions are intentionally dropped (see BE change request:
          // `.claude/cdm-context/ocf-be-change-request-riskfactors.md`).
          riskFactors: {
            smokingStatus: data.smokingStatus,
            vapingStatus: data.vapingStatus,
            weightKg: Number(data.weight),
            heightCm: Number(data.height),
            bmi: Number(data.bmi),
            waistCircumferenceCm: Number(data.waistCircumference),
            qrisk3Score: Number(data.qRisk3Score),
          },
          physicalExam: {
            pulseRate: Number(data.pulse),
            pulseRhythm: data.pulseRhythm,
            systolicBloodPressure: Number(data.bp1),
            diastolicBloodPressure: Number(data.bp2),
          },
          laboratoryInvestigations: {
            haemoglobin: Number(data.haemoglobin),
            totalCholesterol: Number(data.totalCholesterol),
            hdlCholesterol: Number(data.hdlCholesterol),
            nonHdlCholesterol: Number(data.nonHdlCholesterol),
            ldlCholesterol: data.ldlCholesterol,
            triglycerides: Number(data.triglycerides),
            hba1c: Number(data.hba1c),
            serumCreatinine: Number(data.serumCreatinine),
            egfr: data.egfr,
            // ACR — pending BE (see KIN ticket); ignored until OCF DTO adds `acr`.
            acr: data.acr === "Not Available" ? "Not Available" : Number(data.acr) || null,
            // NT-proBNP — hidden until lab capacity is increased (KIN-1050); ignored until OCF DTO adds it.
            ntProBNP:
              data.ntProBNP === "Not Available"
                ? "Not Available"
                : Number(data.ntProBNP) || null,
          },
        }
      ).catch((error) => {
        // Editing a saved review: BE validate 409s on eligibility — treat as
        // passed and let submit handle the correction.
        if (isCorrectionEligibilityConflict(error, data)) {
          return null;
        }

        throw error;
      });

      // Synthetic pass for an edit-mode 409 — the caller still reads
      // suggestedOutcome to advance the step.
      if (!response) {
        return {
          isValid: true,
          suggestedOutcome: {
            outcome: data.ocfSuggestedOutcome || data.outcome || "NORMAL",
            reason: data.ocfSuggestedOutcomeReason ?? null,
          },
        };
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
    },
    onError: handleNetworkError,
  });
};
